"use client";

import {
  finishPremiumInviteLayoutAssignment,
  getPremiumInviteLayoutFromPercent,
  type PremiumInviteLayout,
  PREMIUM_INVITE_LAYOUT_STORAGE_KEY,
  readPostHogPremiumInviteLayout,
} from "@/lib/premiumInviteLayoutAb";
import {
  type PremiumInvitePublicConfig,
  fetchPremiumInvitePublicConfig,
  PREMIUM_INVITE_CONFIG_UPDATED_LS_KEY,
} from "@/lib/premiumInvitePublicConfig";
import { useEffect, useState } from "react";

/**
 * Resolves which surface `/invitacion-premium` shows (BioTemplatePremiumSimple vs ReelsFeed).
 * Loads admin/DB config from GET /api/premium-invite-config (see admin panel).
 * Al volver a la pestaña (`visibilitychange` / bfcache `pageshow`) se vuelve a cargar la config para reflejar cambios de admin sin redeploy.
 *
 * Precedence:
 * URL → env → admin force_layout → admin experiment off → session → PostHog (if enabled) → rollout % (DB, else env).
 */
export function usePremiumInviteLayout(): {
  layout: PremiumInviteLayout;
  ready: boolean;
  publicConfig: PremiumInvitePublicConfig | null;
} {
  const [publicConfig, setPublicConfig] = useState<PremiumInvitePublicConfig | null>(null);
  const [configFetched, setConfigFetched] = useState(false);
  const [state, setState] = useState<{
    layout: PremiumInviteLayout;
    ready: boolean;
  }>({ layout: "premium_simple", ready: false });

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const cfg = await fetchPremiumInvitePublicConfig();
        if (!cancelled) setPublicConfig(cfg);
      } catch {
        if (!cancelled) setPublicConfig(null);
      } finally {
        if (!cancelled) setConfigFetched(true);
      }
    };
    void run();
    const onVisibility = () => {
      if (cancelled || document.visibilityState !== "visible") return;
      void run();
    };
    const onPageShow = (e: PageTransitionEvent) => {
      if (cancelled || !e.persisted) return;
      void run();
    };
    const onStorage = (e: StorageEvent) => {
      if (cancelled || e.key !== PREMIUM_INVITE_CONFIG_UPDATED_LS_KEY) return;
      void run();
    };
    const poll = window.setInterval(() => { if (!cancelled) void run(); }, 20_000);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pageshow", onPageShow);
    window.addEventListener("storage", onStorage);
    return () => {
      cancelled = true;
      window.clearInterval(poll);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pageshow", onPageShow);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  useEffect(() => {
    if (!configFetched) return;

    const url = new URLSearchParams(window.location.search)
      .get("invite_layout")
      ?.trim()
      .toLowerCase();
    if (url === "reels" || url === "reels_feed") {
      finishPremiumInviteLayoutAssignment("reels_feed", "url_param");
      setState({ layout: "reels_feed", ready: true });
      return;
    }
    if (url === "simple" || url === "premium_simple") {
      finishPremiumInviteLayoutAssignment("premium_simple", "url_param");
      setState({ layout: "premium_simple", ready: true });
      return;
    }

    const env = import.meta.env.VITE_PREMIUM_INVITE_LAYOUT_OVERRIDE?.trim().toLowerCase();
    if (env === "reels" || env === "reels_feed") {
      finishPremiumInviteLayoutAssignment("reels_feed", "env_override");
      setState({ layout: "reels_feed", ready: true });
      return;
    }
    if (env === "simple" || env === "premium_simple") {
      finishPremiumInviteLayoutAssignment("premium_simple", "env_override");
      setState({ layout: "premium_simple", ready: true });
      return;
    }

    const cfg = publicConfig;

    if (cfg?.force_layout === "premium_simple" || cfg?.force_layout === "reels_feed") {
      finishPremiumInviteLayoutAssignment(cfg.force_layout, "admin_force");
      setState({ layout: cfg.force_layout, ready: true });
      return;
    }

    if (cfg && cfg.experiment_enabled === false) {
      finishPremiumInviteLayoutAssignment("premium_simple", "admin_disabled");
      setState({ layout: "premium_simple", ready: true });
      return;
    }

    try {
      const cached = sessionStorage.getItem(PREMIUM_INVITE_LAYOUT_STORAGE_KEY);
      if (cached === "reels_feed" || cached === "premium_simple") {
        setState({ layout: cached, ready: true });
        return;
      }
    } catch {
      /* no-op */
    }

    let settled = false;

    const finish = (
      layout: PremiumInviteLayout,
      source: Parameters<typeof finishPremiumInviteLayoutAssignment>[1]
    ) => {
      if (settled) return;
      settled = true;
      finishPremiumInviteLayoutAssignment(layout, source);
      setState({ layout, ready: true });
    };

    const usePosthog = cfg?.use_posthog_layout !== false;

    const tryPosthog = (): boolean => {
      if (!usePosthog) return false;
      const phLayout = readPostHogPremiumInviteLayout();
      if (phLayout) {
        finish(phLayout, "posthog");
        return true;
      }
      return false;
    };

    if (tryPosthog()) return;

    const ph = (
      window as Window & { posthog?: { onFeatureFlags?: (cb: () => void) => void } }
    ).posthog;
    const rolloutFromDb = cfg != null ? cfg.reels_rollout_percent : null;

    const timer = window.setTimeout(() => {
      if (settled) return;
      const layout = getPremiumInviteLayoutFromPercent(rolloutFromDb);
      finish(layout, "percentage");
    }, 2000);

    ph?.onFeatureFlags?.(() => {
      if (settled) return;
      if (tryPosthog()) {
        window.clearTimeout(timer);
      }
    });

    return () => {
      window.clearTimeout(timer);
    };
  }, [configFetched, publicConfig]);

  return { ...state, publicConfig };
}
